<template>
  <ul class="hds-list" @dragover="drag.onDragOverList">
    <li
      v-for="row in drag.displayRows.value"
      :key="row.key"
      :data-hds-key="row.key"
      class="hds-list__item"
      :class="{
        'is-selected': row.key === selectedKey,
        'is-dragging': drag.draggingId.value === row.key,
        'is-inline': variant === 'inline-edit',
      }"
      :draggable="variant === 'inline-edit' ? armedKey === row.key : true"
      @dragstart="drag.onDragStart(row, $event)"
      @dragover="drag.onDragOverRow(row, $event)"
      @dragend="handleDragEnd"
      @click="handleClick(row.key)"
    >
      <span
        class="hds-list__handle"
        title="드래그하여 순서 변경"
        @pointerdown="onHandleDown(row.key)"
        @pointerup="armedKey = null"
        >⋮⋮</span
      >
      <template v-if="variant === 'inline-edit'">
        <el-input
          :model-value="row.label"
          size="small"
          class="hds-list__input"
          @update:model-value="emit('update:label', row.key, $event)"
          @click.stop
        />
      </template>
      <template v-else>
        <span class="hds-list__label">{{ row.label }}</span>
        <span v-if="row.sublabel" class="hds-list__sub">{{ row.sublabel }}</span>
      </template>
      <span v-if="row.tags?.length" class="hds-list__tags">
        <el-tag v-for="t in row.tags" :key="t.text" size="small" :type="t.type ?? 'info'">{{
          t.text
        }}</el-tag>
      </span>
      <span class="hds-list__actions" @click.stop>
        <el-button text size="small" :disabled="indexOf(row.key) === 0" @click="move(row.key, -1)"
          >↑</el-button
        >
        <el-button
          text
          size="small"
          :disabled="indexOf(row.key) === items.length - 1"
          @click="move(row.key, 1)"
          >↓</el-button
        >
        <el-button text size="small" type="danger" @click="emit('remove', row.key)">✕</el-button>
      </span>
    </li>
    <li v-if="items.length === 0" class="hds-list__empty">{{ emptyText ?? '항목이 없습니다.' }}</li>
  </ul>
</template>

<script setup lang="ts">
/**
 * 문서 서식 디자이너 공용 정렬 리스트 — 섹션/필드·컬럼/체크항목·고정행·합계행에 재사용.
 * 네이티브 HTML5 DnD(코어 `useDragReorderList`) + ↑↓ 접근성 폴백.
 * variant 'inline-edit' 는 행 안에서 라벨을 직접 수정하고, 핸들을 잡아야만 드래그가 무장된다.
 */
import { ref, watch, type Ref } from 'vue';
import { useDragReorderList } from '../../../composables/useDragReorderList';
import type { SortableRow } from './sortable-row.js';

type DragRow = SortableRow & { id: string };

const props = defineProps<{
  items: SortableRow[];
  selectedKey?: string | null | undefined;
  variant?: 'default' | 'inline-edit' | undefined;
  emptyText?: string | undefined;
}>();

const emit = defineEmits<{
  (e: 'update:items', items: SortableRow[]): void;
  (e: 'select', key: string): void;
  (e: 'remove', key: string): void;
  (e: 'update:label', key: string, label: string): void;
}>();

const variant = props.variant ?? 'default';
const armedKey = ref<string | null>(null);

// useDragReorderList 는 row.id 기반 — key 를 id 로 별칭한 shallow copy 를 유지한다.
const rowsRef = ref<DragRow[]>([]) as Ref<DragRow[]>;
watch(
  () => props.items,
  (v) => {
    rowsRef.value = v.map((r) => ({ ...r, id: r.key }));
  },
  { immediate: true },
);

const drag = useDragReorderList<DragRow>(rowsRef, (orderedIds) => {
  const byKey = new Map(props.items.map((r) => [r.key, r]));
  emit(
    'update:items',
    orderedIds.map((id) => byKey.get(id)).filter((r): r is SortableRow => Boolean(r)),
  );
  return Promise.resolve();
});

function onHandleDown(key: string): void {
  if (variant === 'inline-edit') armedKey.value = key;
}

async function handleDragEnd(): Promise<void> {
  await drag.onDragEnd();
  armedKey.value = null;
  // HTML5 DnD 는 드롭 후 click 이벤트를 발생시키지 않는다 — 가드를 남기면
  // 다음 진짜 클릭 1회가 삼켜지므로 여기서 바로 소거한다.
  drag.resetClickGuard();
}

function handleClick(key: string): void {
  if (drag.shouldSkipClick()) {
    drag.resetClickGuard();
    return;
  }
  emit('select', key);
}

function indexOf(key: string): number {
  return props.items.findIndex((r) => r.key === key);
}

function move(key: string, dir: number): void {
  const idx = indexOf(key);
  const target = idx + dir;
  if (idx < 0 || target < 0 || target >= props.items.length) return;
  const next = [...props.items];
  const [row] = next.splice(idx, 1);
  if (!row) return;
  next.splice(target, 0, row);
  emit('update:items', next);
}
</script>

<style scoped>
.hds-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.hds-list__item {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--el-border-color);
  border-radius: 4px;
  padding: 4px 6px;
  cursor: pointer;
  font-size: 12px;
  background: var(--el-bg-color);
  user-select: none;
  transition:
    transform 0.12s,
    opacity 0.12s,
    background 0.12s;
}
.hds-list__item.is-inline {
  cursor: default;
}
.hds-list__item.is-selected {
  border-color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}
.hds-list__item.is-dragging {
  opacity: 0.45;
  border-style: dashed;
  border-color: var(--el-color-primary-light-5);
  transform: scale(0.98);
  background: var(--el-color-primary-light-9);
}
.hds-list__handle {
  flex: 0 0 auto;
  cursor: grab;
  color: var(--el-text-color-placeholder);
  font-size: 11px;
  letter-spacing: -1px;
  padding: 0 2px;
}
.hds-list__item:active .hds-list__handle {
  cursor: grabbing;
}
.hds-list__label {
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hds-list__sub {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--el-text-color-secondary);
  font-size: 11px;
}
.hds-list__input {
  flex: 1;
}
.hds-list__tags {
  display: flex;
  gap: 2px;
  flex: 0 0 auto;
}
.hds-list__actions {
  margin-left: auto;
  flex: 0 0 auto;
  display: flex;
}
.hds-list__actions .el-button {
  padding: 2px 4px;
  margin: 0;
  min-height: 24px;
}
.hds-list__empty {
  font-size: 12px;
  color: var(--el-text-color-placeholder);
  text-align: center;
  padding: 10px 0;
  border: 1px dashed var(--el-border-color);
  border-radius: 4px;
}
</style>
